import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck, ShoppingBag, CreditCard, UserPlus, Package, Inbox } from 'lucide-react';
import api from '@/lib/api';

/**
 * Dynamic notification bell — additive UI that can be dropped into any header.
 * - Logged-in users see their own notifications
 * - Admin/staff see shared "admin" inbox (new orders, payments, signups)
 * Polls every 30s; dropdown shows last 30 items, with mark-all-read + per-item read.
 *
 * Intentionally does NOT change any page layout or design.
 */
const ICONS = {
    order_new: ShoppingBag,
    order_paid: CreditCard,
    order_placed: Package,
    order_status: Package,
    user_new: UserPlus,
    low_stock: Package,
};

function timeAgo(iso) {
    if (!iso) return '';
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

export default function NotificationBell({ tone = 'light', testId = 'notification-bell' }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(false);
    const ref = useRef(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/notifications?limit=30');
            setItems(data.items || []);
            setUnread(data.unread_count || 0);
        } catch {
            // silent — 401 etc just means not logged in
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const markOne = async (id) => {
        setItems(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        setUnread(u => Math.max(0, u - 1));
        try { await api.post(`/notifications/${id}/read`); } catch {}
    };

    const markAll = async () => {
        setItems(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnread(0);
        try { await api.post('/notifications/read-all'); } catch {}
    };

    const btnClass = tone === 'dark'
        ? 'text-gray-200 hover:bg-white/10'
        : 'text-gray-700 hover:bg-pink-50';

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => { setOpen(o => !o); if (!open) load(); }}
                className={`relative p-2 rounded-full transition-colors ${btnClass}`}
                aria-label="Notifications"
                data-testid={testId}
            >
                <Bell className="w-5 h-5" />
                {unread > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 bg-pink-600 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center cart-pulse"
                        data-testid="notification-unread-count"
                    >
                        {unread > 99 ? '99+' : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
                    data-testid="notification-dropdown"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <div>
                            <p className="font-heading text-sm font-bold text-gray-900">Notifications</p>
                            <p className="text-[11px] font-body text-gray-500">
                                {unread > 0 ? `${unread} unread` : 'You are all caught up'}
                            </p>
                        </div>
                        {items.length > 0 && unread > 0 && (
                            <button
                                type="button"
                                onClick={markAll}
                                className="text-[11px] font-body font-medium text-pink-600 hover:text-pink-700 flex items-center gap-1"
                                data-testid="notification-mark-all-read"
                            >
                                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                            </button>
                        )}
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        {loading && items.length === 0 && (
                            <div className="flex items-center justify-center py-10 text-gray-400" data-testid="notification-loading">
                                <div className="w-5 h-5 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        )}
                        {!loading && items.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-10 text-gray-400" data-testid="notification-empty">
                                <Inbox className="w-10 h-10 mb-2 opacity-60" />
                                <p className="text-sm font-body">No notifications yet</p>
                            </div>
                        )}
                        {items.map((n, idx) => {
                            const Icon = ICONS[n.type] || Bell;
                            const body = (
                                <div
                                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 transition-colors hover:bg-pink-50/40 ${n.is_read ? '' : 'bg-pink-50/60'}`}
                                    data-testid={`notification-item-${idx}`}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${n.is_read ? 'bg-gray-100 text-gray-500' : 'bg-pink-100 text-pink-600'}`}>
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-body font-semibold text-gray-900 leading-tight truncate">{n.title}</p>
                                        {n.message && <p className="text-xs font-body text-gray-600 leading-snug mt-0.5 line-clamp-2">{n.message}</p>}
                                        <p className="text-[10px] font-body text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                                    </div>
                                    {!n.is_read && (
                                        <span className="w-2 h-2 rounded-full bg-pink-600 mt-2 flex-shrink-0" aria-label="unread" />
                                    )}
                                </div>
                            );
                            const onClick = () => { if (!n.is_read) markOne(n.id); setOpen(false); };
                            return n.link ? (
                                <Link key={n.id} to={n.link} onClick={onClick} className="block">
                                    {body}
                                </Link>
                            ) : (
                                <button key={n.id} type="button" onClick={onClick} className="block w-full text-left">
                                    {body}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
